import React, { useState, useEffect } from "react";
import Avatar from "../Avatar/Avatar";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import verticalDots from "../../assets/verticalDots.svg";
import {
  currentOrganizationOwnerSelector,
  currentOrganizationSelector,
  organizationUsersSelector,
  userSelector,
} from "../../store/selectors";
import { userRoles } from "../../helpers/constants";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as AccessesIcon } from "../../assets/AccessesIcon.svg";
import { inviteUserToOrganization } from "../../store/usersSlice";
import {
  addUserToProject,
  deleteProjectRole,
  fetchProjectUsers,
  removeUserFromProject,
} from "../../store/projectsSlice";
import { OrbitProgress } from "react-loading-indicators";
import AccessPopup from "./AccessPopup";
import UserSuggestionDropdown from "../CommentsBlock/UserSuggestionDropdown";
import CloseButton from "../CloseButton/CloseButton";
import toast from "react-hot-toast";
import useIsMobile from "../../hooks/useIsMobile";
import { useNavigate } from "react-router";

const arrowImageString = `url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4 6L8 10L12 6' stroke='%23717b8e' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`;

const modalStyles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.75)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 102000,
  },
  modal: {
    width: "570px",
    backgroundColor: "#1f242e",
    borderRadius: "16px",
    padding: "32px",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    maxHeight: "80vh",
    overflowY: "auto",
  },
  pill: {
    background: "transparent",
    color: "#3496FF",
    borderRadius: "24px",
    padding: 0,
    fontSize: 20,
    alignSelf: "flex-start",
    fontWeight: 500,
    // marginTop: 5,
    // marginBottom: 25,
    display: "inline-block",
  },
  inviteRow: {
    display: "flex",
    alignItems: "stretch",
    width: "100%",
    gap: 9,
    marginBottom: "20px",
  },
  inviteInputWrap: {
    display: "flex",
    flex: 1,
    border: "1px solid #333b4b",
    borderRadius: "80px",
    overflow: "hidden",
  },
  inviteInput: {
    flex: 1,
    height: "auto",
    padding: "12px 16px",
    // borderTopLeftRadius: "80px",
    // borderBottomLeftRadius: "80px",
    borderRadius: 80,
    backgroundColor: "transparent",
    border: "none",
    color: "#F1F7FF",
    fontSize: "14px",
    fontWeight: 500,
  },
  inviteDivider: {
    width: "1px",
    backgroundColor: "#333b4b",
    margin: "12px 0",
  },
  inviteSelect: {
    padding: "12px 16px",
    backgroundColor: "transparent",
    border: "none",
    color: "#717b8e",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
    appearance: "none",
    backgroundImage: arrowImageString,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
    textAlign: "right",
    paddingRight: "36px",
    WebkitTapHighlightColor: "transparent",
    WebkitTouchCallout: "none",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    msUserSelect: "none",
    userSelect: "none",
    outline: "none",
    textAlign: "right",
  },
  inviteBtn: {
    marginLeft: "8px",
    padding: "12px 24px",
    backgroundColor: "#e8f2ff",
    borderRadius: "80px",
    border: "none",
    color: "#1f242e",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  inviteBtnDisabled: {
    marginLeft: "8px",
    padding: "12px 24px",
    backgroundColor: "#4a4f5c",
    borderRadius: "80px",
    border: "none",
    color: "#717b8e",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "default",
    whiteSpace: "nowrap",
  },
  memberList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    maxHeight: "300px",
    overflowY: "auto",
    overflowX: "hidden",
    width: "100%",
    marginTop: "8px",
    scrollbarWidth: "none" /* Firefox */,
    msOverflowStyle: "none" /* IE and Edge */,
    "&::-webkit-scrollbar": {
      /* Chrome, Safari and Opera */ display: "none",
    },
  },
  memberRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "8px",
    borderRadius: "8px",
    boxSizing: "border-box",
    width: "100%",
    background: "#232733",
    minHeight: "53.5px",
  },
  avatar: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    flexShrink: 0,
  },
  email: {
    flex: 1,
    color: "#c1c4cb",
    fontSize: "16px",
    fontWeight: 500,
    textAlign: "left",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  select: {
    padding: "8px 0 8px 16px",
    backgroundColor: "transparent",
    border: "none",
    color: "#717b8e",
    marginBottom: 3,
    fontSize: "14px",
    fontWeight: 500,
  },
  selectOwner: {
    color: "#c1c4cb",
    cursor: "default",
    backgroundImage: "none",
  },
  rowInviteButton: {
    padding: "8px 24px",
    backgroundColor: "white",
    borderRadius: 80,
    fontSize: 14,
    cursor: "pointer",
  },
};

const UsersPopup = ({
  isOpen,
  onClose,
  isFromExplorer = false,
  isOrg = false,
  isViewer = false,
  isCommenter = false,
  projectInfo,
  usersArray = [],
  usersLoading = false,
  video = null,
  updateUsersArray, // added prop to handle user deletion
}) => {
  // State for context menu (anchor and target user)
  const [menuAnchor, setMenuAnchor] = useState(null); // anchor = {userId, left, top}
  const [menuUser, setMenuUser] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const user = useSelector(userSelector);

  // Handle Escape key to close popup
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Handle click outside menu
  useEffect(() => {
    if (!menuAnchor) return;
    const handleClickOutside = (e) => {
      // Close if click is outside
      if (
        menuAnchor &&
        e.target &&
        !e.target.closest(".user-context-menu") &&
        !e.target.closest(".user-list-dots-btn")
      ) {
        e.preventDefault();
        e.stopPropagation();
        setMenuAnchor(null);
        setMenuUser(null);
      }
    };
    window.addEventListener("mousedown", handleClickOutside);
    return () => window.removeEventListener("mousedown", handleClickOutside);
  }, [menuAnchor]);

  const [newMemberEmail, setNewMemberEmail] = useState("");
  const currentOrganization = useSelector(currentOrganizationSelector);
  const currentOrganizationOwner = useSelector(
    currentOrganizationOwnerSelector
  );
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const { t, i18n } = useTranslation();
  const [newMemberRole, setNewMemberRole] = useState("ADMIN");
  const [accessPopupUser, setAccessPopupUser] = useState(null);
  const [usersFullArray, setUsersFullArray] = useState([]);
  const [inviteSuggestedEmailLoading, setInviteSuggestedEmailLoading] =
    useState(null);

  useEffect(() => {
    if (usersLoading) return;
    if (isFromExplorer) {
      dispatch(fetchProjectUsers(projectInfo.id)).then((res) => {
        if (!res.error) {
          let resultArray = res.payload;
          var filteredResultArray = resultArray.filter(
            (value, index, array) =>
              array.findIndex((val) => val.email === value.email) === index
          );
          const savedEmails =
            JSON.parse(localStorage.getItem("saved_emails")) || [];
          const processedSavedEmails = savedEmails.map((email) => ({
            email,
            isToInvite: true,
          }));
          let finalRes = [...filteredResultArray];
          for (let i = 0; i < processedSavedEmails.length; i++) {
            if (
              !filteredResultArray.find(
                (userInfo) => userInfo.email === processedSavedEmails[i].email
              )
            ) {
              finalRes = [processedSavedEmails[i], ...resultArray];
            }
          }

          const filteredRes = finalRes.filter((usr) => {
            if (!usr.allowedContentVersionIds) return true;
            if (!usr.allowedContentVersionIds.length) return true;
            if (!projectInfo.isFolder) return false;
            return usr.allowedContentVersionIds.includes(projectInfo.id);
          });
          setUsersFullArray(filteredRes);
        }
      });
      return;
    }
    const savedEmails = JSON.parse(localStorage.getItem("saved_emails")) || [];
    const processedSavedEmails = savedEmails.map((email) => ({
      email,
      isToInvite: true,
    }));
    let resultArray = [...usersArray];
    for (let i = 0; i < processedSavedEmails.length; i++) {
      if (
        !usersArray.find(
          (userInfo) => userInfo.email === processedSavedEmails[i].email
        )
      ) {
        resultArray = [processedSavedEmails[i], ...resultArray];
      }
    }

    const filteredArray = resultArray.filter((usr) => {
      if (!usr.allowedContentVersionIds) return true;
      if (!usr.allowedContentVersionIds.length) return true;
      if (!video) return false;
      return usr.allowedContentVersionIds.includes(video.id);
    });
    setUsersFullArray(filteredArray);
  }, [usersArray, usersArray.length, projectInfo.id]);

  if (!isOpen) return null;

  const validateEmail = (email) => {
    return email.match("^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$");
  };

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setNewMemberEmail(email);
    setIsEmailValid(validateEmail(email));
  };

  const onPressInviteSuggestedEmail = (email) => {
    if (email === inviteSuggestedEmailLoading) return;
    setInviteSuggestedEmailLoading(email);
    let userData = {
      email: email,
      role: "ADMIN",
      status: "ACTIVE",
    };

    if (video) {
      userData = {
        ...userData,
        versionId: video.id,
      };
    }

    dispatch(
      addUserToProject({
        projectId: projectInfo.version ? projectInfo.projectId : projectInfo.id,
        userData,
      })
    ).then((res) => {
      if (!res.error) {
        updateUsersArray();
      }
      setInviteSuggestedEmailLoading(null);
    });
  };

  const handleInviteUser = () => {
    setIsLoading(true);
    let userData = {
      email: newMemberEmail,
      role: newMemberRole,
      status: "ACTIVE",
    };

    if (video) {
      userData = {
        ...userData,
        versionId: video.id,
      };
    }

    const currentSavedEmailsArray =
      JSON.parse(localStorage.getItem("saved_emails")) || [];

    const currentSavedEmailIndex = currentSavedEmailsArray.findIndex(
      (email) => email === newMemberEmail
    );

    if (currentSavedEmailIndex < 0) {
      let newSavedEmails = [...currentSavedEmailsArray, newMemberEmail];
      localStorage.setItem("saved_emails", JSON.stringify(newSavedEmails));
    }

    dispatch(
      addUserToProject({
        projectId: projectInfo.version ? projectInfo.projectId : projectInfo.id,
        userData,
      })
    ).then((res) => {
      if (!res.error) {
        updateUsersArray();
        setNewMemberEmail("");
      }
      setIsLoading(false);
    });
  };

  const getProjectTitle = () => {
    if (!projectInfo) return "Loading...";
    const projectPath = projectInfo.path;
    if (!projectPath) return projectInfo.title;
    let projectTitle = projectPath.reduce((res, current) => {
      res += `${current.title} • `;
      return res;
    }, "");

    if (projectInfo.version) {
      projectTitle += `${t("video.version")} ${projectInfo.version}`;
    } else {
      projectTitle = projectTitle.slice(0, -3);
    }

    return projectTitle;
  };

  const handleDotsClick = (event, member) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    let newMenuAnchor = {
      userId: member.userId,
      id: member.id,
      left: rect.right + window.scrollX - 5,
      top: rect.bottom + window.scrollY - 26,
    };
    if (isMobile) {
      newMenuAnchor = {
        userId: member.userId,
        id: member.id,
        left: rect.right + window.scrollX - 115,
        top: rect.bottom + window.scrollY,
      };
    }
    setMenuAnchor(newMenuAnchor);
    setMenuUser(member);
  };

  const handleDelete = (roleId) => {
    onPressDeleteUser(roleId);
  };

  const openAccessModal = (user) => {
    setAccessPopupUser(user);
    setMenuAnchor(null);
  };

  const handleChangeUserRole = (member, newRole) => {
    dispatch(deleteProjectRole(member.id)).then((res) => {
      if (!res.error) {
        let userData = {
          email: member.email,
          role: newRole,
          status: "ACTIVE",
        };

        if (video) {
          userData = {
            ...userData,
            versionId: video.id,
          };
        }

        dispatch(
          addUserToProject({
            projectId: projectInfo.version
              ? projectInfo.projectId
              : projectInfo.id,
            userData,
          })
        ).then((res2) => {
          if (!res2.error) {
            updateUsersArray();
          }
        });
      }
    });
  };

  const renderUserInfo = (member, isOwner) => {
    if (!member) return null;
    const myRole =
      user &&
      user.projectRoles &&
      user.projectRoles.find(
        (role) =>
          role.projectId === projectInfo.projectId ||
          role.projectId === projectInfo.id
      );
    const orgRole =
      user &&
      user.organizationRoles &&
      user.organizationRoles.find(
        (org) => org.organizationId === currentOrganization.id
      );
    const amIOwner =
      member.role !== "COMMENTER" &&
      member.role !== "VIEWER" &&
      orgRole &&
      orgRole.role === "OWNER" &&
      member.userId === user.userId;

    const canChangeRole =
      orgRole && orgRole.role === "OWNER" && user.email != member.email;
    // const canChangeRole = true;

    return (
      <div key={member.userId || member.email} style={modalStyles.memberRow}>
        <span style={modalStyles.email}>{member.email}</span>
        {!member.isToInvite && (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            {!canChangeRole && (
              <span style={modalStyles.select}>
                {amIOwner ? "Владелец" : userRoles[member.role]}
                {/* {`${myRole && myRole.id == member.id ? " (это вы)" : member.status !== "ACTIVE" ? " (Приглашён)" : ""}`} */}
                {`${myRole && myRole.id == member.id ? " (это вы)" : ""}`}
              </span>
            )}
            {canChangeRole && (
              <div>
                <select
                  value={member.role}
                  defaultValue={"ADMIN"}
                  onChange={(e) => handleChangeUserRole(member, e.target.value)}
                  style={{
                    ...modalStyles.inviteSelect,
                    paddingRight: isMobile ? 32 : 36,
                    paddingLeft: isMobile ? 5 : 12,
                    textAlign: isMobile ? "center" : "right",
                    MozAppearance: "none",
                    WebkitAppearance: "none",
                  }}
                >
                  <option value="ADMIN">Администратор</option>
                  <option style={{ textAlign: "right" }} value="COMMENTER">
                    Комментатор
                  </option>
                  <option style={{ textAlign: "right" }} value="VIEWER">
                    Зритель
                  </option>
                </select>
              </div>
            )}
            {(!member.isToInvite &&
              user.userId !== member.userId &&
              !isViewer &&
              !isCommenter && (
                <button
                  className="user-list-dots-btn"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    borderRadius: "50%",
                    marginLeft: 5,
                    width: 28,
                    height: 28,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "background 0.2s",
                  }}
                  onClick={(e) => handleDotsClick(e, member)}
                  aria-label="More actions"
                >
                  <img
                    src={verticalDots}
                    alt="menu"
                    style={{ width: 18, height: 18 }}
                  />
                </button>
              )) || <div style={{ width: 12, height: 28 }} />}
          </div>
        )}
        {!member.isToInvite &&
          menuAnchor &&
          menuAnchor.userId === member.userId && (
            <div
              className="user-context-menu"
              style={{
                position: "fixed",
                top: menuAnchor.top,
                left: menuAnchor.left,
                zIndex: 9999,
                boxShadow: "0 4px 16px rgba(17,20,23,0.18)",
                background: "#262b36",
                borderRadius: 10,
                padding: "8px 0",
                minWidth: 110,
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
              }}
            >
              <button
                style={{
                  background: "none",
                  border: "none",
                  color: "white",
                  fontWeight: 500,
                  fontSize: 15,
                  padding: "5px 5px",
                  display: "flex",
                  textAlign: "left",
                  alignItems: "center",
                  paddingLeft: 20,
                  borderRadius: 0,
                  justifyContent: "flex-start",
                  textAlign: "left",
                  cursor: "pointer",
                  borderRadius: 8,
                  transition: "background 0.1s",
                }}
                onClick={() => openAccessModal(member)}
              >
                Доступ к...
              </button>
              <button
                style={{
                  background: "none",
                  border: "none",
                  color: "#FF4D4F",
                  fontWeight: 500,
                  fontSize: 15,
                  padding: "5px 5px",
                  display: "flex",
                  paddingLeft: 20,
                  alignItems: "center",
                  textAlign: "left",
                  justifyContent: "flex-start",
                  textAlign: "left",
                  cursor: "pointer",
                  borderRadius: 8,
                  transition: "background 0.1s",
                }}
                onClick={() => handleDelete(menuAnchor.id)}
              >
                {t("mainScreen.upload.delete") || "Удалить"}
              </button>
            </div>
          )}
        {member.isToInvite && !isViewer && !isCommenter && (
          <div
            style={modalStyles.rowInviteButton}
            onClick={() => onPressInviteSuggestedEmail(member.email)}
          >
            {inviteSuggestedEmailLoading === member.email ? (
              <OrbitProgress style={{ fontSize: 8 }} color="black" />
            ) : (
              "Пригласить"
            )}
          </div>
        )}
      </div>
    );
  };

  const onPressDeleteUser = (roleId) => {
    setMenuAnchor(null);
    if (confirm("Вы действительно хотите удалить пользователя?")) {
      dispatch(deleteProjectRole(roleId)).then((res) => {
        if (!res.error) {
          updateUsersArray();
          toast.success("Пользователь удален из проекта");
        }
      });
    }
  };

  const page = (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 3,
              marginRight: 28,
              cursor: "pointer",
            }}
            onClick={() => navigate("/team")}
          >
            <AccessesIcon />
            <span
              style={{
                fontFamily: "NT Somic",
                color: "#717B8E",
                fontSize: 14,
              }}
            >
              Все доступы
            </span>
          </div>
          <CloseButton
            styles={{ height: 12, top: 36 }}
            size={12}
            transform="scale(1.9)"
            onClose={onClose}
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            width: "100%",
            marginBottom: 20,
            // marginTop: -24,
            pointerEvents: "none",
            gap: 7,
          }}
        >
          <h2
            style={{
              color: "#c1c4cb",
              fontSize: 20,
              fontWeight: 500,
              // alignSelf: "flex-start",
              padding: 0,
              margin: 0,
            }}
          >
            Команда {}
          </h2>
          <div style={modalStyles.pill}>{getProjectTitle()}</div>
        </div>

        {!isViewer && !isCommenter && (
          <div
            style={{
              ...modalStyles.inviteRow,
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            <div style={modalStyles.inviteInputWrap}>
              <input
                type="email"
                value={newMemberEmail}
                onChange={handleEmailChange}
                placeholder={
                  isMobile ? "Введите почту" : "Введите электронную почту"
                }
                style={{
                  ...modalStyles.inviteInput,
                  paddingRight: isMobile ? 10 : 16,
                }}
              />
              {/* <div style={modalStyles.inviteDivider} />
              <select
                value={newMemberRole}
                defaultValue={"ADMIN"}
                onChange={(e) => setNewMemberRole(e.target.value)}
                style={{
                  ...modalStyles.inviteSelect,
                  paddingRight: isMobile ? 32 : 36,
                  paddingLeft: isMobile ? 5 : 12,
                  textAlign: isMobile ? "center" : "right",
                  MozAppearance: "none",
                  WebkitAppearance: "none",
                }}
              >
                <option value="ADMIN">Админ</option>
                <option style={{ textAlign: "right" }} value="EDITOR">
                  Редактор
                </option>
              </select> */}
            </div>
            <button
              style={
                isEmailValid
                  ? modalStyles.inviteBtn
                  : modalStyles.inviteBtnDisabled
              }
              onClick={() => {
                if (isEmailValid && !isLoading) {
                  handleInviteUser();
                }
              }}
              disabled={!isEmailValid}
            >
              {isLoading ? "Загрузка..." : "Пригласить"}
            </button>
          </div>
        )}
        <div style={modalStyles.memberList}>
          {usersFullArray.length && !usersLoading ? (
            usersFullArray.map((member, index) => {
              return renderUserInfo(member, !!currentOrganizationOwner);
            })
          ) : (
            <OrbitProgress style={{ fontSize: 8 }} color="rgb(166, 134, 237)" />
          )}
        </div>
        <AccessPopup
          isOpen={!!accessPopupUser}
          editedUser={accessPopupUser}
          onSave={() => updateUsersArray()}
          onClose={() => setAccessPopupUser(null)}
        />
      </div>
    </div>
  );

  return ReactDOM.createPortal(page, document.body);
};

export default UsersPopup;
